import { useEffect, useMemo, useState } from 'react';
import type { PointerEvent } from 'react';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import HelpOutline from '@mui/icons-material/HelpOutline';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import type { RequiredUserAction } from '../types';
import { formatPhone } from '../utils/phone';

function getErrorMessage(error: unknown, fallback: string): string {
  const maybeError = error as {
    response?: { data?: { reason?: string | string[]; error?: string; detail?: string } };
    message?: string;
  };
  const reason = maybeError?.response?.data?.reason;
  if (Array.isArray(reason) && reason.length > 0) {
    return String(reason[0]);
  }
  if (typeof reason === 'string' && reason.trim()) {
    return reason;
  }
  return maybeError?.response?.data?.error
    || maybeError?.response?.data?.detail
    || maybeError?.message
    || fallback;
}

function resolveModalAction(actions: RequiredUserAction[]): RequiredUserAction | null {
  return actions.find((action) => action.status === 'pending') ?? null;
}

function getPayloadString(action: RequiredUserAction | null, key: string): string | null {
  const value = action?.payload?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function formatDateTime(value: string | null, locale: string): string | null {
  if (!value) {
    return null;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsedDate);
}

function translateCallType(t: (key: string) => string, callType: string | null): string | null {
  if (!callType) {
    return null;
  }

  const knownTypes: Record<string, string> = {
    ai_agent: 'requiredActions.manualHangupReason.callTypes.aiAgent',
    reverse_ivr: 'requiredActions.manualHangupReason.callTypes.reverseIvr',
    sip: 'requiredActions.manualHangupReason.callTypes.sip',
  };
  return t(knownTypes[callType] ?? 'requiredActions.manualHangupReason.callTypes.unknown');
}

function buildOperationalOriginLabel(
  t: (key: string, options?: Record<string, string>) => string,
  action: RequiredUserAction | null,
): string | null {
  const campaignName = getPayloadString(action, 'campaignName');
  if (campaignName) {
    return t('requiredActions.manualHangupReason.origins.campaignWithName', { name: campaignName });
  }

  const origin = getPayloadString(action, 'callOrigin');
  const knownOrigins: Record<string, string> = {
    campaign: 'requiredActions.manualHangupReason.origins.campaign',
    manual: 'requiredActions.manualHangupReason.origins.manual',
    public_api: 'requiredActions.manualHangupReason.origins.publicApi',
  };
  return origin ? t(knownOrigins[origin] ?? 'requiredActions.manualHangupReason.origins.unknown') : null;
}

export default function RequiredUserActionModal() {
  const { t, i18n } = useTranslation('common');
  const { requiredActions, refreshBootstrap } = useAuth();
  const action = useMemo(() => resolveModalAction(requiredActions), [requiredActions]);
  const callId = getPayloadString(action, 'callId') ?? action?.resourceId ?? null;
  const toNumber = getPayloadString(action, 'toNumber');
  const formattedToNumber = toNumber ? formatPhone(toNumber) : null;
  const callType = translateCallType(t, getPayloadString(action, 'callType'));
  const operationalOrigin = buildOperationalOriginLabel(t, action);
  const agentName = getPayloadString(action, 'agentName');
  const endedAt = formatDateTime(
    getPayloadString(action, 'endedAt') ?? getPayloadString(action, 'hangupRequestedAt') ?? action?.createdAt ?? null,
    i18n.language,
  );
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setReason('');
    setError(null);
    setSaving(false);
  }, [action?.id]);

  const handleSave = async () => {
    if (!action || saving) {
      return;
    }

    const normalizedReason = reason.trim();
    if (!normalizedReason) {
      setError(t('requiredActions.manualHangupReason.reasonRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/auth/me/required-actions/${action.id}/resolve/`, {
        reason: normalizedReason,
      });
      await refreshBootstrap();
      setReason('');
    } catch (requestError) {
      const responseReason = (requestError as { response?: { data?: { reason?: string } } })?.response?.data?.reason;
      if (responseReason === 'required_action_already_resolved') {
        await refreshBootstrap();
        return;
      }
      setError(getErrorMessage(requestError, t('requiredActions.manualHangupReason.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleSavePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType !== 'touch') {
      return;
    }

    event.preventDefault();
    void handleSave();
  };

  const isManualHangupReason = action?.type === 'manual_hangup_reason';

  return (
    <Dialog
      open={Boolean(action)}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      onClose={(_event, reasonValue) => {
        if (reasonValue === 'backdropClick' || reasonValue === 'escapeKeyDown') {
          return;
        }
      }}
      aria-labelledby="required-user-action-title"
    >
      <DialogTitle
        id="required-user-action-title"
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <AssignmentTurnedInIcon color="primary" />
        {isManualHangupReason
          ? t('requiredActions.manualHangupReason.title')
          : t('requiredActions.defaultTitle')}
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {isManualHangupReason
            ? t(
              endedAt
                ? 'requiredActions.manualHangupReason.descriptionWithEndedAt'
                : 'requiredActions.manualHangupReason.description',
              {
                callId: callId ?? t('requiredActions.manualHangupReason.unknownCall'),
                endedAt,
              },
            )
            : t('requiredActions.defaultDescription')}
        </Typography>
        {isManualHangupReason ? (
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr' },
              gap: 1,
              bgcolor: 'background.default',
            }}
          >
            {[
              [t('requiredActions.manualHangupReason.callLabel'), callId],
              [t('requiredActions.manualHangupReason.endedAtLabel'), endedAt],
              [t('requiredActions.manualHangupReason.callTypeLabel'), callType],
              [t('requiredActions.manualHangupReason.originLabel'), operationalOrigin],
              [t('requiredActions.manualHangupReason.agentLabel'), agentName],
              [t('requiredActions.manualHangupReason.toNumberLabel'), formattedToNumber],
            ].filter(([, value]) => Boolean(value)).map(([label, value]) => (
              <Box key={label} sx={{ display: 'contents' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ overflowWrap: 'anywhere' }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Paper>
        ) : null}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" fontWeight={700}>
            {t('requiredActions.manualHangupReason.reasonLabel')}
          </Typography>
          <Tooltip title={t('requiredActions.manualHangupReason.reasonHelper')} arrow>
            <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <TextField
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t('requiredActions.manualHangupReason.reasonPlaceholder')}
          multiline
          minRows={3}
          inputProps={{ maxLength: 255 }}
          disabled={saving}
          autoFocus
          fullWidth
        />
        {error ? (
          <Alert severity="error" variant="outlined">
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          type="button"
          variant="contained"
          onPointerDown={handleSavePointerDown}
          onClick={() => void handleSave()}
          disabled={saving}
        >
          {saving
            ? t('requiredActions.manualHangupReason.saving')
            : t('requiredActions.manualHangupReason.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
